import { Routes, Route, Navigate, useLocation, useNavigate, type Location } from 'react-router-dom'
import { Suspense, lazy, useEffect, useRef } from 'react'
import { DashboardLayout } from '../layout/DashboardLayout'
import { LoadingScreen } from '@/components/ui/loading-screen'
import { ErrorBoundary } from './ErrorBoundary'
import { useAuth } from './AuthProvider'
import { WelcomeLayout } from '../layout/WelcomeLayout'
import { clearAuthReturnPath, getAuthReturnPath } from '@/lib/auth-return'

// Lazy load pages
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage').then(module => ({ default: module.DashboardPage })))
const AssistantsListPage = lazy(() => import('@/pages/assistants/AssistantsListPage').then(module => ({ default: module.AssistantsListPage })))
const AssistantConsolePage = lazy(() => import('@/pages/assistants/AssistantConsolePage').then(module => ({ default: module.AssistantConsolePage })))
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then(module => ({ default: module.SettingsPage })))
const HelpPage = lazy(() => import('@/pages/help/HelpPage'))
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage').then(module => ({ default: module.ReportsPage })))
const ReconnectPage = lazy(() => import('@/pages/auth/ReconnectPage').then(module => ({ default: module.ReconnectPage })))
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then(module => ({ default: module.LoginPage })))
const SignupPage = lazy(() => import('@/pages/auth/SignupPage').then(module => ({ default: module.SignupPage })))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then(module => ({ default: module.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage').then(module => ({ default: module.ResetPasswordPage })))
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage').then(module => ({ default: module.VerifyEmailPage })))
const MagicLinkPage = lazy(() => import('@/pages/auth/MagicLinkPage').then(module => ({ default: module.MagicLinkPage })))
const UninvitedPage = lazy(() => import('@/pages/auth/UninvitedPage').then(module => ({ default: module.UninvitedPage })))
const WelcomePage = lazy(() => import('@/pages/dashboard/WelcomePage').then(module => ({ default: module.WelcomePage })))
const NursingPage = lazy(() => import('@/pages/dashboard/NursingPage').then(module => ({ default: module.NursingPage })))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(module => ({ default: module.NotFoundPage })))
const TermsOfServicePage = lazy(() => import('@/pages/legal/TermsOfServicePage').then(module => ({ default: module.TermsOfServicePage })))
const PrivacyPolicyPage = lazy(() => import('@/pages/legal/PrivacyPolicyPage').then(module => ({ default: module.PrivacyPolicyPage })))
const IntegrationsPage = lazy(() => import('@/pages/integrations/IntegrationsPage').then(module => ({ default: module.IntegrationsPage })))
const OAuthCallbackPage = lazy(() => import('@/pages/integrations/OAuthCallbackPage').then(module => ({ default: module.OAuthCallbackPage })))

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, error } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }
  if (error) {
    return null; // ApiErrorPage is handled by AuthProvider
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

const getRedirectPath = (location: Location) => getAuthReturnPath(location.state)

function RootRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  console.log('[RootRedirect] Component rendered. location.pathname:', location.pathname);

  useEffect(() => {
    // Only redirect once to prevent double redirects in StrictMode
    if (hasRedirected.current) {
      console.log('[RootRedirect] Already redirected, skipping');
      return;
    }

    console.log('[RootRedirect] useEffect running');
    const path = getRedirectPath(location);
    console.log('[RootRedirect] Will redirect to:', path);

    // Mark as redirected before navigating
    hasRedirected.current = true;

    // Cleanup storage if it exists
    clearAuthReturnPath(path)

    navigate(path, { replace: true });
  }, [location, navigate]);

  return <LoadingScreen />;
}

function RedirectIfAuthenticated({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (!loading && user && !hasRedirected.current) {
      const path = getRedirectPath(location);
      console.log('[RedirectIfAuthenticated] User is authenticated. Redirecting to:', path);

      hasRedirected.current = true;

      clearAuthReturnPath(path)

      navigate(path, { replace: true });
    }
  }, [user, loading, location, navigate]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    return <LoadingScreen />; // Show loading while redirecting
  }

  return <>{children}</>;
}

export function AppRoutes() {
  const { user, loading } = useAuth();

  console.log('[AppRoutes] Rendering. user:', !!user, 'loading:', loading, 'pathname:', window.location.pathname);

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/auth/reconnect/:assistantId" element={<PrivateRoute><ReconnectPage /></PrivateRoute>} />
          <Route path="/login" element={
            <RedirectIfAuthenticated>
              <LoginPage />
            </RedirectIfAuthenticated>
          } />
          <Route path="/signup" element={
            <RedirectIfAuthenticated>
              <SignupPage />
            </RedirectIfAuthenticated>
          } />
          <Route path="/forgot-password" element={
            <RedirectIfAuthenticated>
              <ForgotPasswordPage />
            </RedirectIfAuthenticated>
          } />
          <Route path="/magic-link" element={
            <RedirectIfAuthenticated>
              <MagicLinkPage />
            </RedirectIfAuthenticated>
          } />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/" element={
            loading ? (
              <LoadingScreen />
            ) : user ? (
              <RootRedirect />
            ) : (
              <WelcomePage />
            )
          } />
          <Route path="/uninvited" element={<UninvitedPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/nursing" element={<WelcomeLayout><NursingPage /></WelcomeLayout>} />
          <Route path="/help" element={<WelcomeLayout><HelpPage /></WelcomeLayout>} />
          <Route element={
            <PrivateRoute>
              <DashboardLayout />
            </PrivateRoute>
          }>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/dashboard/assistants" element={<AssistantsListPage />} />
            <Route path="/dashboard/reports" element={<ReportsPage />} />
            <Route path="/dashboard/integrations" element={<IntegrationsPage />} />
            <Route path="/dashboard/settings" element={<SettingsPage />} />
            <Route path="/dashboard/home" element={<WelcomePage />} />
            <Route path="/nursing" element={<NursingPage />} />
          </Route>
          <Route path="/dashboard/assistants/agent/:encodedBotId" element={
            <PrivateRoute>
              <AssistantConsolePage />
            </PrivateRoute>
          } />
          <Route path="/integrations/callback" element={
            <PrivateRoute>
              <OAuthCallbackPage />
            </PrivateRoute>
          } />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
